import React, { useEffect, useState } from 'react';
import { Card, EmployeeCardContext } from '@op/titan.insurance-ecard-js/';

import RestApi from '../api/RestApi';
import {
    ERROR_ID_EXPIRED_CARD,
    ERROR_ID_INVALID_URL,
} from '../constants/common';
import { useCardLanguage } from '../context/CardLanguageContext';
import { useAsyncError } from '../hooks/useAsyncError';

const OLD_ID_LENGTH: number = 40;
const NEW_ID_LENGTH: number = 64;
const RETRY_COUNT: number = 3;

const BOOKMARK_MARKER: string = '_';

const parseCardIdFromPath = () => {
    const params = new URLSearchParams(window.location.search);
    // cardId should be the first parameter without a key
    const cardIdValue = params && (params.keys().next().value as string);

    if (!cardIdValue) {
        throw new Error('No cardId', {
            cause: ERROR_ID_INVALID_URL,
        });
    }

    // Support 'old' links that have _cv in the end becaue of reasons.
    const cardId = cardIdValue.split(BOOKMARK_MARKER)[0];

    if (!isValidCardId(cardId)) {
        throw new Error(`Invalid cardId [${cardId}]`, {
            cause: ERROR_ID_INVALID_URL,
        });
    }

    return cardId;
};

const isValidCardId = (id: string) => {
    return id && (id.length === OLD_ID_LENGTH || id.length === NEW_ID_LENGTH);
};

type CtxProviderProps = {
    children: React.ReactNode;
};

export const EmployeeCardContextProvider = ({ children }: CtxProviderProps) => {
    const [cardState, setCardState] = useState<Card | null>(null);
    const cardLanguageState = useCardLanguage();

    const throwError = useAsyncError();

    // fetch eCard from backend API
    const fetchCard = async (cardId: string) => {
        // Wrapper to retry fetching card
        // Fetching cards policies in the backend takes some time and RD-Proxy cuts the request after timeout(5)
        // Therefore we need to retry few times.
        const retryWrapper = (retry: number) => {
            RestApi.getCard(cardId, cardLanguageState?.language)
                .then((response) => {
                    if (!response?.identifier) {
                        // if response is empty card has expired
                        throwError(
                            new Error(`Empty card with id [${cardId}]`, {
                                cause: ERROR_ID_EXPIRED_CARD,
                            }),
                        );
                    }

                    setCardState(response!);
                })
                .catch((error) => {
                    if (retry > 0) {
                        retryWrapper(--retry);
                        return;
                    }
                    // bakcoff and throw error
                    throwError(error);
                });
        };

        retryWrapper(RETRY_COUNT);
    };

    useEffect(() => {
        fetchCard(parseCardIdFromPath());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardLanguageState]);

    return (
        <EmployeeCardContext.Provider value={cardState}>
            {children}
        </EmployeeCardContext.Provider>
    );
};
